import { Message } from "src/modules/communication-protobuf";
import debug from "src/modules/debug-logger";
import RtcManager from "src/modules/rtc/RtcManager";
import createOpusPlayer from "src/modules/audio-processing/createOpusPlayer";
import createOpusRecorder from "src/modules/audio-processing/createOpusRecorder";
import { getVideoConstraints } from "src/modules/rtc/media-constraints";
import AppStatus from "src/constants/AppStatus";

const log = window.require("electron-log");

export default class ParentStationRtcManager extends RtcManager {
  constructor({ messenger, pingService, onRecreateConnection, getActivityEmitter }) {
    super({ messenger, isPolite: false });
    this.getActivityEmitter = getActivityEmitter;
    this.onRecreateConnection = onRecreateConnection;
    this.messenger.on(Message.WebRTCCandidate.name, this.handleWebRTCCandidates);
    this.messenger.on(Message.WebRTCCreateOffer.name, this.handleOffer);
    this.messenger.on(Message.WebRTCCreateAnswer.name, this.handleAnswer);
    this.messenger.on(Message.AudioPacket.name, this.onAudioPacket);
    this.messenger.on(Message.MotionInfoPacket.name, this.onMotionInfoPacket);
    this.appStatus = AppStatus.STARTED;
    pingService.on("ping-response", this.checkRtcConnection);
    this.on("connection-state-change", this.handleConnectionStateChange);
  }

  async initializeConnection(babyJid) {
    const peer = this.initializePeer(babyJid);
    peer.opusPlayer = await createOpusPlayer();
    // await this.createAndSendOfferTo(peer);
    return peer;
  }

  handleConnectionStateChange(peerJid) {
    const baby = this.getPeer(peerJid);
    if (baby == null || baby.peerConnection == null) return;
    if (baby.peerConnection.connectionState === "disconnected") {
      debug.parentStation("closing peerConnection, connectionState === disconnected", peerJid);
      baby.peerConnection.close();
    }
  }

  checkRtcConnection = async (peerJid) => {
    const baby = this.getPeer(peerJid);
    if (baby == null || baby.peerConnection == null) return;

    const { connectionState } = baby.peerConnection;
    if (connectionState === "closed") {
      debug.parentStation("restaring peerConnection, connectionState === closed", peerJid);
      baby.isInVideoMode = false;
      this.onRecreateConnection();
      this.recreateConnection({ peerJid: baby.jid, delayVideoReconnect: true });
    }
  };

  async initializeAudioTransmission() {
    this.opusRecorder = await createOpusRecorder({
      onFlush: this.onAudioDataFlush
    });
    return this.opusRecorder;
  }

  onAudioDataFlush = (buffer) => {
    const peers = Object.values(this.peers);

    const message = Message.AudioPacket.create({
      codec: "OPUS",
      data: new Uint8Array(buffer)
    });

    peers.forEach(({ jid }) => {
      this.messenger.sendAsEnvelope({
        to: jid,
        payload: { message }
      });
    });
  };

  async initializeVideoStream() {
    try {
      const videoStream = await navigator.mediaDevices.getUserMedia(
        getVideoConstraints({ videoQuality: "HIGH" }).constraints
      );
      const videoTrack = videoStream.getVideoTracks()[0];

      if (videoTrack) {
        this.videoStream = videoStream;
        this.videoTrack = videoTrack;
        return videoStream;
      }
      return null;
    } catch (error) {
      log.error(error);
      return null;
    }
  }

  onMotionInfoPacket = (empty, from) => {
    let baby = this.getPeer(from);
    if (baby == null) return;
    const activityEmitter = this.getActivityEmitter();
    if (activityEmitter) activityEmitter.handleMotion();
  };

  onAudioPacket = ({ data, listening, flush, codec }, from) => {
    let baby = this.getPeer(from);
    if (baby == null || baby.opusPlayer == null) return;

    baby.opusPlayer.onData(data);
  };

  stopSendingVideoToBaby = async (jid) => {
    const peer = this.getPeer(jid);
    peer.videoTransceiver.direction = "inactive";
    peer.isInVideoMode = false;
    this.videoTrack.stop();
    peer.videoTrack && peer.videoTrack.stop();
    this.videoTrack = null;
    this.videoStream = null;
    peer.videoTransceiver = null;
  };

  destroyVideoStream = () => {
    this.videoTrack && this.videoTrack.stop();
    this.videoTrack = null;
    this.videoStream = null;
    Object.values(this.peers).forEach((peer) => peer.videoTrack && peer.videoTrack.stop());
  };

  onAppStatusChange = (appStatus) => {
    this.appStatus = appStatus;
  };

  waitForConnectedState = (timeout, peerJid) => {
    let timeoutId;
    let clear;

    const promise = new Promise((resolve, reject) => {
      if (this.getPeer(peerJid).peerConnection.connectionState === undefined) {
        resolve(true);
        return;
      }
      if (this.getPeer(peerJid).peerConnection.connectionState === "connected") {
        resolve(true);
        return;
      }

      const handleConnectionStateChange = () => {
        if (this.getPeer(peerJid).peerConnection.connectionState === "connected") {
          clearTimeout(timeoutId);
          clear = () => {};
          this.getPeer(peerJid).peerConnection.removeEventListener(
            "connectionstatechange",
            handleConnectionStateChange
          );
          resolve(true);
        }
      };

      timeoutId = setTimeout(() => {
        this.getPeer(peerJid).peerConnection.removeEventListener("connectionstatechange", handleConnectionStateChange);
        clear = () => {};
        reject();
      }, timeout);

      this.getPeer(peerJid).peerConnection.addEventListener("connectionstatechange", handleConnectionStateChange);

      clear = () => {
        clearTimeout(timeoutId);
        this.getPeer(peerJid).peerConnection.removeEventListener("connectionstatechange", handleConnectionStateChange);
        resolve(false);
        clear = () => {};
      };
    });

    return { promise, clear };
  };

  waitForCommDataChannel = (timeout, peerJid) => {
    let timeoutId;
    let clear;

    const promise = new Promise((resolve, reject) => {
      if (this.getPeer(peerJid).commDataChannel.readyState === "open") {
        resolve(true);
        return;
      }

      const onDataChannel = () => {
        clearTimeout(timeoutId);
        clear = () => {};
        this.getPeer(peerJid).commDataChannel.removeEventListener("open", onDataChannel);
        resolve(true);
      };

      timeoutId = setTimeout(() => {
        this.getPeer(peerJid).commDataChannel.removeEventListener("open", onDataChannel);
        clear = () => {};
        reject();
      }, timeout);

      this.getPeer(peerJid).commDataChannel.addEventListener("open", onDataChannel);

      clear = () => {
        clearTimeout(timeoutId);
        this.getPeer(peerJid).commDataChannel.removeEventListener("open", onDataChannel);
        resolve(false);
        clear = () => {};
      };
    });

    return { promise, clear };
  };

  removeListeners = () => {
    debug.rtcManager("about to remove event listeners");
    const { messenger } = this;
    messenger.removeListener(Message.AudioPacket.name, this.onAudioPacket);
    messenger.removeListener(Message.MotionInfoPacket.name, this.onMotionInfoPacket);
    messenger.removeListener(Message.WebRTCCreateOffer.name, this.handleOffer);
    messenger.removeListener(Message.WebRTCCreateAnswer.name, this.handleAnswer);
    messenger.removeListener(Message.WebRTCCandidate.name, this.handleWebRTCCandidates);
  };
  destroy() {
    this._destroy();
  }
}
